<template>
  <section class="mx-auto py-6 md:py-10">
    <h2
      class="text-center sm:text-left section-heading sm:w-full mx-auto sm:mx-0"
    >
      {{ headerText }}
    </h2>

    <div
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-9 gap-y-8 md:gap-y-10 mt-8 mb-8 md:mb-10"
    >
      <template v-if="isLoading">
        <div v-for="i in props.cardCount" :key="i">
          <UtilsSkeletonLoading class="rounded-lg h-40 w-full mb-4 card-img" />
          <UtilsSkeletonLoading class="w-full h-14 mb-3" />
          <UtilsSkeletonLoading class="w-full h-12" />
        </div>
      </template>
      <template v-else-if="contentsData.length === 0">
        <p class="text-bw-900 text-base">No Case Studies found</p>
      </template>
      <template v-else>
        <ArticleCard
          v-for="content in contentsData"
          :key="content.id"
          :with-logo="true"
          :logo="
            content.attributes?.study_case_client?.data?.attributes?.image?.data
              ?.attributes?.url
          "
          :title="content.attributes?.card_title"
          :desc="content.attributes?.layout_component?.[0]?.description"
          :image-url="content.attributes?.image?.data?.attributes?.url"
          :slug="`/work/${content.attributes?.slug}`"
        />
      </template>
    </div>

    <div class="mx-auto text-center">
      <NuxtLink
        :to="{
          path: '/work',
          query: route.path.includes('/expertises')
            ? { expertise: filterExpertise }
            : route.path.includes('/industries')
              ? { industry: filterIndustry }
              : {}
        }"
        title="Case Studies"
        class="btn btn-md md:btn-lg btn-primary-reverse w-max mx-auto"
      >
        View Case Studies
      </NuxtLink>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  headerText: {
    type: String,
    required: true
  },
  cardCount: {
    type: Number,
    default: 9
  },
  detailId: {
    type: Number,
    default: null
  },
  filterIndustry: {
    type: String,
    default: null
  },
  filterExpertise: {
    type: String,
    default: null
  },
  filterExpertises: {
    type: Array,
    default: null
  },
  filterSolution: {
    type: String,
    default: null
  },
  filterProduct: {
    type: String,
    default: null
  }
})

const isLoading = ref(true)
const route = useRoute()

const contentsData = ref([])

const getContents = async () => {
  const query = {
    'pagination[pageSize]': props.cardCount,
    populate:
      'image,layout_component,layout_component.type,study_case_client.image,solutions,products',
    'filters[layout][$eq]': 'study-case',
    sort: 'publish_at:desc'
  }

  if (props.detailId) {
    query['filters[$and][0][id][$ne]'] = props.detailId
  }

  if (props.filterIndustry) {
    query['filters[$or][1][study_case_client][industry][title][$in]'] =
      props.filterIndustry
  }

  if (props.filterExpertises) {
    props.filterExpertises.forEach((item, index) => {
      query[`filters[$or][2][expertices][id][$in][${index}]`] = item.id
    })
  } else if (props.filterExpertise) {
    query[`filters[$or][2][expertices][title][$in]`] = props.filterExpertise
  }

  if (props.filterSolution) {
    query[`filters[$or][3][solutions][title][$in]`] = props.filterSolution
  }

  if (props.filterProduct) {
    query[`filters[$or][4][products][title][$in]`] = props.filterProduct
  }

  const { data, error, pending } = await useSuitmediaApiData('/api/contents', {
    query
  })
  isLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Case Studies does not exist`
    })
  }

  return data.value
}

const contents = await getContents()
contentsData.value = contents.data
</script>

<style lang="scss" scoped></style>
